<template>
    <div
        v-if="data"
        ref="gridEl"
        v-qa="'product-grid'"
        v-qa.fetching="fetching"
        class="product-grid"
    >
        <div
            ref="gridTopBarWrapperEl"
            :class="classGridTopBarWrapperFinal"
            class="grid-top-bar-wrapper"
        >
            <div class="grid-top-bar">
                <div class="left">
                    <FilterButton
                        :active-filter-count
                        @open-filters-modal="$emit('open-filters-modal')"
                    />

                    <div
                        v-qa="'total-results'"
                        class="total-results"
                    >
                        {{ totalResultsCountLabel }}
                    </div>
                </div>

                <div class="right">
                    <SortingDropdown
                        :model="sortBy"
                        :options="sortOptions"
                        :route-getter="getRouteSort"
                    />
                </div>
            </div>
        </div>

        <div
            :class="classGrid"
            class="grid"
        >
            <FilterChips
                v-qa="'filter-chips-page'"
                :chips="filterChips"
                :route-reset
            />

            <div class="cards">
                <ProductCard
                    v-for="(item, index) in data"
                    :key="item.catalogueCode"
                    :item
                    :variations="getVariations(item)"
                    class="card"
                    :image-loading="index < 4 ? 'eager' : 'lazy'"
                    :image-fetch-priority="
                        index < 4 ? 'high' : index < 8 ? 'medium' : 'low'
                    "
                    @link-clicked="
                        (clickedItem) => trackClicked(clickedItem, index)
                    "
                />

                <SecondaryEditorialBox
                    v-if="showBanners && banner1"
                    :data="banner1"
                />

                <PrimaryEditorialBox
                    v-if="showBanners && banner2"
                    :data="banner2"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup generic="T extends ProductGridFragment">
import { computed, toRef, defineAsyncComponent, hydrateOnVisible } from 'vue'
import { useI18n } from 'vue-i18n'

import {
    type Banner1Fragment,
    type Banner2Fragment,
    CatalogueSortOrder,
    type ProductCardFragment,
    type ProductGridFragment,
    type ProductListAnalyticsFragment,
} from '@/graphql/api'

import { assertNever } from '@/helpers'
import { useProductGridStyling } from './useProductGridStyling'
import { useStickyGridFiltersBar } from '@/components/composables/useStickyGridFiltersBar'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { useNumberFormatter } from '@/composables/useNumberFormatter'

// import SecondaryEditorialBox from '@/components/cms/component/grid/SecondaryEditorialBox.vue'
// import PrimaryEditorialBox from '@/components/cms/component/grid/PrimaryEditorialBox.vue'
import SortingDropdown, {
    type DropdownOption,
} from '@/components/ui/SortingDropdown.vue'
import FilterButton from '@/components/product/FilterButton.vue'
import FilterChips, {
    type FilterChip,
} from '@/components/ui/filter/FilterChips.vue'

const ProductCard = defineAsyncComponent({
    loader: () => import('@/components/layout/cards/ProductCard.vue'),
    hydrate: hydrateOnVisible(),
})

const SecondaryEditorialBox = defineAsyncComponent({
    loader: () =>
        import('@/components/cms/component/grid/SecondaryEditorialBox.vue'),
    hydrate: hydrateOnVisible({
        rootMargin: '50px',
    }),
})

const PrimaryEditorialBox = defineAsyncComponent({
    loader: () =>
        import('@/components/cms/component/grid/PrimaryEditorialBox.vue'),
    hydrate: hydrateOnVisible({
        rootMargin: '50px',
    }),
})

const props = defineProps<{
    data: T[]
    page: number
    fullGridAt: number
    resultsCount: number
    fetching: boolean
    banner1?: Banner1Fragment
    banner2?: Banner2Fragment
    sortBy: CatalogueSortOrder
    getRouteSort: (sortBy: CatalogueSortOrder) => ButtonRouteRelative
    activeFilterCount: number
    filterChips: FilterChip[]
    routeReset: ButtonRouteRelative
    trackClicked: (product: ProductListAnalyticsFragment, index: number) => void
    isER?: boolean
}>()

defineEmits<{
    'open-filters-modal': []
}>()

const getVariations = (
    item: T
): (ProductCardFragment & ProductListAnalyticsFragment)[] | undefined => {
    switch (item.__typename) {
        case 'CatalogueEngagementRing':
            return item.variationsByPrimaryMetal
        case 'CatalogueWeddingRing':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
        // fallthrough
        case 'CatalogueJewelleryRing':
            return item.variations
        case 'CatalogueReadyToGoRing':
            return undefined
        default:
            return assertNever(item, undefined)
    }
}

const numberFormatter = useNumberFormatter(0)
const { t } = useI18n()

const sortOptions: DropdownOption<CatalogueSortOrder>[] = [
    {
        value: CatalogueSortOrder.Recommended,
        label: 'Recommended',
    },
    {
        value: CatalogueSortOrder.PriceAscending,
        label: 'Price (low to high)',
    },
    {
        value: CatalogueSortOrder.PriceDescending,
        label: 'Price (high to low)',
    },
]

const totalResultsCountLabel = computed(() =>
    t('catalogue.grid.results', props.resultsCount, {
        named: { count: numberFormatter.format(props.resultsCount) },
    })
)

const fullGrid = computed(() => props.data.length === props.fullGridAt)
const showBanners = computed(() => props.page === 1 && fullGrid.value)

const { classGrid } = useProductGridStyling(toRef(() => props.fetching))

const {
    gridTopBarWrapperEl,
    gridEl,
    classGridTopBarWrapper,
    gridTopBarHeight,
} = useStickyGridFiltersBar()

const classGridTopBarWrapperFinal = computed(() => ({
    ...classGridTopBarWrapper.value,
    'er': props.isER,
}))
</script>

<style lang="scss" scoped>
.product-grid {
    margin: 0 0 60px;

    .grid-top-bar-wrapper {
        width: 100%;
        height: v-bind(gridTopBarHeight);
        padding: 20px;
        display: flex;
        justify-content: space-between;
        position: sticky;
        z-index: 20;
        top: 0;
        background: $white;
        transition: box-shadow 0.1s ease-in;
        transition: background-color 0.1s ease-in;

        &.er {
            @include min-width(1024) {
                position: relative;
                top: 0;
            }
        }

        @include min-width(360) {
            padding: 20px 0;
        }

        @include min-width(1024) {
            top: 110px;
        }

        &.stuck {
            box-shadow: 0 2px 5px rgba($black, 0.1);

            @include min-width(1024) {
                box-shadow: none;
                background-color: $stone-2;
            }
        }

        .grid-top-bar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            width: 1440px;
            max-width: 100%;
            margin: 0 auto;
            font-family: $effra-font-stack;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-size: 0.875rem;

            .left {
                display: flex;
                margin: 5px 0;
            }

            .right {
                display: flex;
                margin: 5px 0;
            }

            @include min-width(360) {
                padding: 0 20px;
            }

            @include min-width(1280) {
                padding: 0 40px;
            }

            .total-results {
                display: none;
                color: $grey-400;

                @include min-width(640) {
                    display: block;
                }
            }
        }
    }

    .grid {
        display: flex;
        flex-wrap: wrap;
        width: 1440px;
        max-width: 100%;
        margin: 0 auto;

        @include fetching;

        @include min-width(360) {
            padding: 0 20px;
        }

        @include min-width(1280) {
            padding: 0 40px;
        }

        .cards {
            flex-wrap: wrap;
            display: grid;
            grid-auto-rows: auto;
            grid-template-columns: repeat(1, 1fr);
            gap: 32px 0;

            @include min-width(640) {
                grid-template-columns: repeat(2, 1fr);
                width: 100%;
                gap: 32px 20px;
            }

            @include min-width(768) {
                grid-template-columns: repeat(3, 1fr);
            }

            @include min-width(1024) {
                grid-template-columns: repeat(4, 1fr);
            }
        }
    }
}
</style>
